import React, { useState, useEffect, useRef } from 'react';
import { notifyActiveEventsUpdated } from '../../../../../utils/activeEventsBus';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  GeoJSON,
  useMap,
  FeatureGroup,
} from 'react-leaflet';
import { EditControl } from "react-leaflet-draw";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import axios from 'axios';
import L from 'leaflet';
import * as turf from '@turf/turf';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
} from '@mui/material';
import { MapHomeControl, MapInfoControl } from "../../../../common/MapControls/MapControls";
import { useLiveLocation } from "../../../../common/useLiveLocation";
import CircularProgress from "@mui/material/CircularProgress";

window.L = L;

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Submitting a felt report now records who submitted it, so it requires a
// logged-in session - the same JWT every other authenticated call in this
// app already attaches.
const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Reusable Components
const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
    {text}
  </Typography>
);

const FieldLabel = ({ text, required }) => (
  <FormLabel sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>
    {text}{required && <Box component="span" sx={{ color: '#ef4444', ml: 0.5 }}>*</Box>}
  </FormLabel>
);

// Custom Marker icons for Two Points
const createNumberedIcon = (number) => {
  return L.divIcon({
    className: 'custom-number-icon',
    html: `<div class="number-circle">${number}</div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 25],
    popupAnchor: [0, -25],
  });
};

const icon1 = createNumberedIcon(1);
const icon2 = createNumberedIcon(2);

// Polygon drawing style: black dashed outline (no fill), small plain white
// vertex dots instead of leaflet-draw's default larger blue markers.
const polygonVertexIcon = L.divIcon({
  className: 'felt-report-polygon-vertex',
  iconSize: [6, 6],
});
const polygonShapeOptions = { color: '#000000', weight: 2, dashArray: '4,4', fillOpacity: 0 };

// Pans the map to a geolocated position - only fires on "Use My Location"
// (via the `target` prop's changing `ts`), never on a plain map click/drag,
// so manually placing/adjusting the marker never yanks the view around.
const PanToLatLng = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lon], 17);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.ts]);
  return null;
};

// Component to handle the custom two-point logic
const TwoPointMapHandler = ({ twoPoints, setTwoPoints, setFormData, drawMode, onPointMoved }) => {
    useMapEvents({
        click(e) {
            if (drawMode === 'twoPoints') {
                setTwoPoints(prevPoints => {
                    const newPoints = [...prevPoints, e.latlng];
                    if (newPoints.length > 2) {
                        return [e.latlng];
                    }
                    return newPoints;
                });
                onPointMoved?.(e.latlng.lat, e.latlng.lng);
            }
        },
    });

    useEffect(() => {
        if (twoPoints.length === 1) {
            setFormData(prev => ({
                ...prev,
                starting_point_lat: twoPoints[0].lat.toFixed(6),
                starting_point_long: twoPoints[0].lng.toFixed(6),
                ending_point_lat: '',
                ending_point_long: '',
                latitude: '',
                longitude: '',
                geojson_feature: null,
            }));
        } else if (twoPoints.length === 2) {
            setFormData(prev => ({
                ...prev,
                starting_point_lat: twoPoints[0].lat.toFixed(6),
                starting_point_long: twoPoints[0].lng.toFixed(6),
                ending_point_lat: twoPoints[1].lat.toFixed(6),
                ending_point_long: twoPoints[1].lng.toFixed(6),
                latitude: '',
                longitude: '',
                geojson_feature: null,
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                starting_point_lat: '',
                starting_point_long: '',
                ending_point_lat: '',
                ending_point_long: '',
            }));
        }
    }, [twoPoints, setFormData]);

    return (
        <>
            {twoPoints.length > 0 && (
                <Marker position={twoPoints[0]} icon={icon1}>
                    <Popup>Point 1</Popup>
                </Marker>
            )}
            {twoPoints.length > 1 && (
                <Marker position={twoPoints[1]} icon={icon2}>
                    <Popup>Point 2</Popup>
                </Marker>
            )}
        </>
    );
};

// Component to handle the single point logic. `onPointMoved` fires on both
// a fresh click and a drag-end of the placed marker, so Municipality/
// Post-Admin/Suco stay in sync with wherever the pin actually ends up.
const FormMap = ({ markerPosition, setMarkerPosition, setFormData, drawMode, onPointMoved, onBufferCleared }) => {
    useMapEvents({
        click(e) {
            if (drawMode === 'singlePoint') {
                setMarkerPosition(e.latlng);
                setFormData((prevData) => ({
                    ...prevData,
                    latitude: e.latlng.lat.toFixed(6),
                    longitude: e.latlng.lng.toFixed(6),
                    starting_point_lat: '',
                    starting_point_long: '',
                    ending_point_lat: '',
                    ending_point_long: '',
                    geojson_feature: null,
                }));
                onBufferCleared?.();
                onPointMoved?.(e.latlng.lat, e.latlng.lng);
            }
        },
    });

    const handleDragEnd = (e) => {
        const latlng = e.target.getLatLng();
        setMarkerPosition(latlng);
        setFormData((prevData) => ({
            ...prevData,
            latitude: latlng.lat.toFixed(6),
            longitude: latlng.lng.toFixed(6),
            geojson_feature: null,
        }));
        onBufferCleared?.();
        onPointMoved?.(latlng.lat, latlng.lng);
    };

    if (!markerPosition) return null;

    if (drawMode === 'singlePoint') {
        return (
            <Marker position={markerPosition} draggable={true} eventHandlers={{ dragend: handleDragEnd }}>
                <Popup>
                    Latitude: {markerPosition.lat.toFixed(6)} <br />
                    Longitude: {markerPosition.lng.toFixed(6)} <br />
                    <em>Drag to adjust — Municipality/Post-Admin/Suco update automatically.</em>
                </Popup>
            </Marker>
        );
    }

    // Two Points/Polygon mode: the marked/live point isn't cleared, just
    // shown masked (low opacity, same pin icon, not draggable, not part of
    // what gets submitted) so it stays visible while drawing around it
    // instead of vanishing on mode switch.
    return (
        <Marker position={markerPosition} opacity={0.45}>
            <Popup>Reference point — not submitted in this mode.</Popup>
        </Marker>
    );
};

// Helper functions to get the current date and time
const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
};

const getCurrentTime = () => {
    const today = new Date();
    const hours = today.getHours().toString().padStart(2, '0');
    const minutes = today.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};


function FeltReport() {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [twoPoints, setTwoPoints] = useState([]);
  const [drawMode, setDrawMode] = useState('singlePoint');
  const [drawnItems, setDrawnItems] = useState(null);
  const [bufferMeters, setBufferMeters] = useState('');
  const [isBufferPolygon, setIsBufferPolygon] = useState(false);
  const featureGroupRef = useRef(null);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [formData, setFormData] = useState({
    location: '',
    latitude: '',
    longitude: '',
    date_cyclone: getCurrentDate(),
    time_cyclone: getCurrentTime(),
    time_type: '',
    wind_speed: '',
    rainfall_amount: '',
    storm_surge_height: '',
    damage_type: [],
    warning_received: '',
    affected_status: '',
    additional_info: '',
    municipality: '',
    sub_municipality: '',
    suco: '',
    starting_point_lat: '',
    starting_point_long: '',
    ending_point_lat: '',
    ending_point_long: '',
    geojson_feature: null,
    people_injured_count: '',
    people_deaths_count: '',
    houses_affected_count: '',
    livestock_lost_count: '',
    estimated_loss_amount: '',
  });

  const [municipalities, setMunicipalities] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [sucos, setSucos] = useState([]);
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState('');

  // "Use My Location" - browser geolocation + adm3-boundary reverse lookup,
  // shared with every other felt-report form. geoFlyTarget only drives the
  // map's one-off flyTo on a fresh geolocation fix, never on a manual
  // click/drag of the marker.
  const { locating, locationError, setLocationError, requestBrowserLocation, resolveAdmFromLatLng, resolveLandmark } = useLiveLocation();
  const [geoFlyTarget, setGeoFlyTarget] = useState(null);
  const [landmarkLoading, setLandmarkLoading] = useState(false);
  const [landmarkNotFound, setLandmarkNotFound] = useState(false);

  // Fills Municipality/Post-Admin/Suco from a resolved adm hierarchy -
  // shared by both the initial geolocation fix and every later manual
  // click/drag of the marker, so the two paths can't drift apart.
  const applyResolvedAdm = (adm) => {
    setSelectedMunicipality(adm.adm1_code);
    setSelectedSubMunicipality(adm.adm2_code);
    setFormData((prev) => ({
      ...prev,
      municipality: adm.adm1_code,
      sub_municipality: adm.adm2_code,
      suco: adm.adm3_code,
    }));
  };

  const handlePointMoved = async (lat, lon) => {
    const adm = await resolveAdmFromLatLng(lat, lon);
    if (adm) {
      applyResolvedAdm(adm);
      setLocationError("");
    } else {
      setLocationError("This point doesn't fall within a known suco boundary - select Municipality/Post-Admin/Suco manually below.");
    }

    setLandmarkLoading(true);
    setLandmarkNotFound(false);
    const landmark = await resolveLandmark(lat, lon);
    setLandmarkLoading(false);
    if (landmark) {
      setFormData((prev) => ({ ...prev, location: landmark }));
    } else {
      setFormData((prev) => ({ ...prev, location: '' }));
      setLandmarkNotFound(true);
    }
  };

  const handleUseMyLocation = async () => {
    try {
      const { lat, lon } = await requestBrowserLocation();
      setDrawMode('singlePoint');
      setTwoPoints([]);
      setDrawnItems(null);
      setBufferMeters(''); setIsBufferPolygon(false);
      setMarkerPosition({ lat, lng: lon });
      setGeoFlyTarget({ lat, lon, ts: Date.now() });
      setFormData((prev) => ({
        ...prev,
        latitude: lat.toFixed(6),
        longitude: lon.toFixed(6),
        starting_point_lat: '',
        starting_point_long: '',
        ending_point_lat: '',
        ending_point_long: '',
        geojson_feature: null,
      }));
      await handlePointMoved(lat, lon);
    } catch (err) {
      // requestBrowserLocation already recorded a user-facing locationError
    }
  };

  // Prompts for location as soon as the form opens - no button to click,
  // same one-shot request handleUseMyLocation always made, just fired on
  // mount instead of a click. If denied/unsupported, locationError guides
  // the user to the manual dropdowns below, which always still work.
  useEffect(() => {
    handleUseMyLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CSS for custom icon
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-number-icon {
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .felt-report-polygon-vertex {
        background-color: #ffffff;
        border: 1px solid #000000;
        border-radius: 50%;
      }
      .number-circle {
        background-color: #2563eb;
        color: white;
        border-radius: 50%;
        width: 25px;
        height: 25px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: bold;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
      .image-popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1300;
      }
      .image-popup-content {
        position: relative;
        background-color: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        max-width: 90%;
        max-height: 90%;
      }
      .image-popup-content img {
        max-width: 100%;
        max-height: 90vh;
        display: block;
        border-radius: 8px;
      }
      .image-popup-close {
        position: absolute;
        top: 10px;
        right: 10px;
        font-size: 24px;
        cursor: pointer;
        color: #fff;
        background-color: rgba(0, 0, 0, 0.6);
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: background-color 0.2s;
      }
      .image-popup-close:hover {
        background-color: rgba(0, 0, 0, 0.8);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    axios
      .post(`${backendUrl}Municipalities/municipalities_list/`)
      .then((response) => {
        setMunicipalities(response.data);
      })
      .catch((error) => console.error('Error fetching municipalities:', error));
  }, []);

  useEffect(() => {
    if (selectedMunicipality) {
      axios
        .post(`${backendUrl}Municipalities/sub_municipalities_list/`, {
          adm2_code: selectedMunicipality,
        })
        .then((response) => {
          setSubMunicipalities(response.data);
        })
        .catch((error) => {
          console.error('Error fetching sub-municipalities:', error);
        });
    } else {
      setSubMunicipalities([]);
      setSucos([]);
    }
  }, [selectedMunicipality]);

  useEffect(() => {
    if (selectedSubMunicipality) {
      axios
        .post(`${backendUrl}Municipalities/suco_list/`, {
          adm2_code: selectedSubMunicipality,
        })
        .then((response) => {
          setSucos(response.data);
        })
        .catch((error) => console.error('Error fetching sucos:', error));
    } else {
      setSucos([]);
    }
  }, [selectedSubMunicipality]);

  const handleMunicipalityChange = (e) => {
    const value = e.target.value;
    setSelectedMunicipality(value);
    setSelectedSubMunicipality('');
    setSucos([]);
    setFormData((prev) => ({
      ...prev,
      municipality: value,
      sub_municipality: '',
      suco: '',
    }));
  };

  const handleSubMunicipalityChange = (e) => {
    const value = e.target.value;
    setSelectedSubMunicipality(value);
    setSucos([]);
    setFormData((prev) => ({
      ...prev,
      sub_municipality: value,
      suco: '',
    }));
  };
  
  const handleSucoChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, suco: value }));
  };

  const handleDrawModeChange = (e) => {
    setDrawMode(e.target.value);
    // markerPosition is deliberately left alone here - it stays visible as
    // a reference dot when switching away from Single Point (FormMap
    // renders it as a masked/low-opacity pin in that case) instead of vanishing.
    setTwoPoints([]);
    setDrawnItems(null);
    setBufferMeters(''); setIsBufferPolygon(false);
    setFormData(prev => ({
        ...prev,
        latitude: '',
        longitude: '',
        starting_point_lat: '',
        starting_point_long: '',
        ending_point_lat: '',
        ending_point_long: '',
        geojson_feature: null,
    }));
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'damage_type') {
        setFormData((prevData) => {
            const newTypes = checked
                ? [...prevData.damage_type, value]
                : prevData.damage_type.filter((vegType) => vegType !== value);
            return { ...prevData, [name]: newTypes };
        });
    } else {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }
  };

  const handleReset = () => {
    setFormData({
      location: '',
      latitude: '',
      longitude: '',
      date_cyclone: getCurrentDate(),
      time_cyclone: getCurrentTime(),
      time_type: '',
      wind_speed: '',
      rainfall_amount: '',
      storm_surge_height: '',
      damage_type: [],
      warning_received: '',
      affected_status: '',
      additional_info: '',
      municipality: '',
      sub_municipality: '',
      suco: '',
      starting_point_lat: '',
      starting_point_long: '',
      ending_point_lat: '',
      ending_point_long: '',
      geojson_feature: null,
      people_injured_count: '',
      people_deaths_count: '',
      houses_affected_count: '',
      livestock_lost_count: '',
      estimated_loss_amount: '',
    });
    setMarkerPosition(null);
    setTwoPoints([]);
    setDrawnItems(null);
    setBufferMeters(''); setIsBufferPolygon(false);
    setDrawMode('singlePoint');
    setSelectedMunicipality('');
    setSelectedSubMunicipality('');
    setSucos([]);
    setSubmitSuccess(false);
    setSubmitError('');
    setGeoFlyTarget(null);
    setLocationError('');
    setLandmarkNotFound(false);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    if (!formData.additional_info || !formData.additional_info.trim()) {
      setSubmitError('Description is required.');
      return;
    }
    const dataToSend = new FormData();

    // Map the dropdown values to a numerical value before sending
    const windSpeedMapping = {
      'Gentle breeze': 1,
      'Moderate breeze': 2,
      'Strong breeze': 3,
      'Gale': 4,
      'Storm': 5,
      'Hurricane': 6
    };
    const rainfallMapping = {
      'Light': 1,
      'Moderate': 2,
      'Heavy': 3,
      'Extreme': 4
    };
    const stormSurgeMapping = {
      '0 to 0.1 metres': 1,
      '0.1 to 0.5 metres': 2,
      '0.5 to 1.25 metres': 3,
      '1.25 to 2.5 metres': 4,
      '2.5 to 4 metres': 5,
      '4 to 6 metres': 6,
      '6 to 9 metres': 7,
      '9 to 14 metres': 8,
      'Over 14 metres': 9
    };
    
    // Add all form data to the FormData object
    for (const key in formData) {
      if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
        if (key === 'damage_type') {
            dataToSend.append(key, formData[key].join(','));
        } else if (key === 'geojson_feature' && formData.geojson_feature) {
            dataToSend.append(key, JSON.stringify(formData.geojson_feature));
        } else if (key === 'wind_speed') {
            dataToSend.append(key, windSpeedMapping[formData[key]]);
        } else if (key === 'rainfall_amount') {
            dataToSend.append(key, rainfallMapping[formData[key]]);
        } else if (key === 'storm_surge_height') {
            dataToSend.append(key, stormSurgeMapping[formData[key]]);
        } else {
            dataToSend.append(key, formData[key]);
        }
      }
    }

    try {
      const response = await axios.post(
        `${backendUrl}cyclone/cyclone-felt-reports/`,
        dataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...authHeaders(),
          },
        }
      );

      if (response.status === 201 || response.status === 200) {
        setSubmitSuccess(true);
        notifyActiveEventsUpdated();
        setTimeout(() => {
          handleReset();
        }, 2000);
      } else {
        setSubmitError('Failed to submit report. Please try again.');
      }
    } catch (error) {
      console.error(
        'There was an error submitting the cyclone report!',
        error.response ? error.response.data : error.message
      );
      const errorMessage = error.response?.data?.detail || JSON.stringify(error.response?.data);
      setSubmitError(`An error occurred while submitting the report: ${errorMessage}`);
    }
  };

  const onCreated = (e) => {
    const { layer } = e;
    
    if (drawnItems) {
      drawnItems.clearLayers();
    }
    // leaflet-draw adds `layer` straight into the real FeatureGroup itself
    // (not into our `drawnItems` copy above) - remove any older shapes from
    // it so a second polygon doesn't leave the first one stuck on the map.
    featureGroupRef.current?.eachLayer((l) => {
      if (l !== layer) featureGroupRef.current.removeLayer(l);
    });

    const newDrawnItems = new L.FeatureGroup();
    newDrawnItems.addLayer(layer);
    setDrawnItems(newDrawnItems);
    setIsBufferPolygon(false);

    const geojson = layer.toGeoJSON();
    setFormData(prev => ({
        ...prev,
        geojson_feature: geojson,
        latitude: '',
        longitude: '',
        starting_point_lat: '',
        starting_point_long: '',
        ending_point_lat: '',
        ending_point_long: '',
    }));

    const center = layer.getBounds().getCenter();
    handlePointMoved(center.lat, center.lng);
  };

  const handleClearPolygon = () => {
    setDrawnItems(null);
    setIsBufferPolygon(false);
    featureGroupRef.current?.clearLayers();
    setFormData(prev => ({ ...prev, geojson_feature: null }));
  };

  // Buffer coordinates are submitted exactly like a hand-drawn polygon - the
  // radio switches to Polygon so that's reflected in the UI too, but the
  // meters field/button stay reachable there so the same button can update
  // the buffer's radius instead of only being able to add it once.
  const handleApplyBuffer = () => {
    const meters = parseFloat(bufferMeters);
    if (!markerPosition || !meters || meters <= 0) return;
    const bufferFeature = turf.buffer(
      turf.point([markerPosition.lng, markerPosition.lat]), meters, { units: 'meters' }
    );
    const layer = L.geoJSON(bufferFeature).getLayers()[0];
    const newDrawnItems = new L.FeatureGroup();
    newDrawnItems.addLayer(layer);
    setDrawnItems(newDrawnItems);
    setIsBufferPolygon(true);
    setFormData(prev => ({ ...prev, geojson_feature: layer.toGeoJSON(), latitude: '', longitude: '' }));
    setDrawMode('polygon');
  };

  const handleClearBuffer = () => {
    setDrawnItems(null);
    setIsBufferPolygon(false);
    featureGroupRef.current?.clearLayers();
    setDrawMode('singlePoint');
    setFormData(prev => ({
      ...prev, geojson_feature: null,
      latitude: markerPosition ? markerPosition.lat.toFixed(6) : prev.latitude,
      longitude: markerPosition ? markerPosition.lng.toFixed(6) : prev.longitude,
    }));
  };

  // Shared styles
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '15px',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: '15px', '&.Mui-focused': { color: '#2563eb' } },
    '& .MuiFormHelperText-root': { fontSize: '12px', mt: 0.25 },
  };

  const radioSx = { color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };
  const checkSx = { p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
            Cyclone Felt Report
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>
            Report cyclone observations and damages
          </Typography>
        </Box>
      </Box>

      {/* Form Body */}
      <Box component="form" onSubmit={handleReportSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {submitSuccess && (
          <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>
            Cyclone Report submitted successfully! Form will reset in 2 seconds.
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>
            {submitError}
          </Alert>
        )}

        {/* Location: details on the left, map on the right */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
          {/* LEFT: Location Information */}
          <Box>
            <SectionLabel text="Location Information" />

            {locating && (
              <Typography sx={{ fontSize: '13px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={14} /> Requesting your location…
              </Typography>
            )}

            {locationError && !(selectedMunicipality && selectedSubMunicipality && formData.suco) && (
              <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>
                {locationError}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2.5 }}>
              <TextField
                label="Municipality"
                select
                size="small"
                fullWidth
                sx={inputSx}
                value={selectedMunicipality}
                onChange={handleMunicipalityChange}
              >
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a municipality</em></MenuItem>
                {municipalities.map((municipality, index) => (
                  <MenuItem key={index} value={municipality.adm1_code}>
                    {municipality.adm1}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Post-Admin"
                select
                size="small"
                fullWidth
                sx={inputSx}
                value={formData.sub_municipality}
                onChange={handleSubMunicipalityChange}
                disabled={!selectedMunicipality}
              >
                <MenuItem value="">
                  <em style={{ color: '#94a3b8' }}>
                    {selectedMunicipality ? 'Select a Post-Admin' : 'Select a municipality first'}
                  </em>
                </MenuItem>
                {subMunicipalities.map((submunicipality, index) => (
                  <MenuItem key={index} value={submunicipality.adm2_code}>
                    {submunicipality.adm2}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Suco"
                select
                size="small"
                fullWidth
                sx={inputSx}
                value={formData.suco}
                onChange={handleSucoChange}
                disabled={!selectedSubMunicipality}
              >
                <MenuItem value="">
                  <em style={{ color: '#94a3b8' }}>
                    {selectedSubMunicipality ? 'Select a suco' : 'Select a Post-Admin first'}
                  </em>
                </MenuItem>
                {sucos.map((suco, index) => (
                  <MenuItem key={index} value={suco.adm3_code}>
                    {suco.adm3}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <FormControl>
                <FieldLabel text="How do you want to define the location?" />
                <RadioGroup row value={drawMode} onChange={handleDrawModeChange}>
                  <FormControlLabel
                    value="singlePoint"
                    control={<Radio size="small" sx={radioSx} />}
                    label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Single Point</Typography>}
                    sx={{ mr: 2 }}
                  />
                  <FormControlLabel
                    value="twoPoints"
                    control={<Radio size="small" sx={radioSx} />}
                    label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Two Points</Typography>}
                    sx={{ mr: 2 }}
                  />
                  <FormControlLabel
                    value="polygon"
                    control={<Radio size="small" sx={radioSx} />}
                    label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Polygon</Typography>}
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Nearest landmark/location"
                size="small"
                fullWidth
                sx={{ ...inputSx, mt: 2 }}
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder={landmarkLoading ? 'Looking up the nearest landmark…' : undefined}
              />
              {landmarkNotFound && !formData.location && (
                <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>
                  No landmark found nearby — add one.
                </Typography>
              )}
            </Box>

            {drawMode === 'singlePoint' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
                <TextField
                  label="Latitude"
                  size="small"
                  fullWidth
                  sx={inputSx}
                  value={formData.latitude}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Longitude"
                  size="small"
                  fullWidth
                  sx={inputSx}
                  value={formData.longitude}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}

            {drawMode === 'singlePoint' && markerPosition && (
              <Box sx={{ mt: 2.5, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <FieldLabel text="Buffer zone around this point (meters)" />
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField size="small" type="number" label="Meters" sx={{ ...inputSx, width: 120 }}
                    value={bufferMeters} onChange={(e) => setBufferMeters(e.target.value)} />
                  <Button size="small" variant="contained" onClick={handleApplyBuffer}
                    disabled={!bufferMeters || parseFloat(bufferMeters) <= 0}
                    sx={{ textTransform: 'none', fontSize: '13px', background: '#2563eb', '&:hover': { background: '#1d4ed8' } }}>
                    Add Buffer Zone
                  </Button>
                </Box>
              </Box>
            )}

            {drawMode === 'twoPoints' && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
                  <TextField
                    label="Starting Latitude"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={formData.starting_point_lat}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Starting Longitude"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={formData.starting_point_long}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
                  <TextField
                    label="Ending Latitude"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={formData.ending_point_lat}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Ending Longitude"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={formData.ending_point_long}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              </>
            )}

            {drawMode === 'polygon' && (
              <Box sx={{ mt: 2.5, p: 2, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                {isBufferPolygon ? (
                  <>
                    <FieldLabel text="Buffer zone around this point (meters)" />
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField size="small" type="number" label="Meters" sx={{ ...inputSx, width: 120 }}
                        value={bufferMeters} onChange={(e) => setBufferMeters(e.target.value)} />
                      <Button size="small" variant="contained" onClick={handleApplyBuffer}
                        disabled={!bufferMeters || parseFloat(bufferMeters) <= 0}
                        sx={{ textTransform: 'none', fontSize: '13px', background: '#2563eb', '&:hover': { background: '#1d4ed8' } }}>
                        Update Buffer Zone
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={handleClearBuffer}
                        sx={{ textTransform: 'none', fontSize: '13px' }}>
                        Clear
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography sx={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>
                      Click the polygon icon on the map to draw the area
                    </Typography>
                    {drawnItems && (
                      <Button size="small" variant="outlined" color="error" onClick={handleClearPolygon}
                        sx={{ mt: 1.5, textTransform: 'none', fontSize: '13px' }}>
                        Clear Polygon
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* RIGHT: Map */}
          <Box>
            <Box sx={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <MapContainer
                center={[-8.7747, 125.7275]}
                zoom={8}
                maxZoom={22}
                style={{ height: '450px', width: '100%' }}
              >
                <MapHomeControl />
                <MapInfoControl text="This form asks for your location when it opens and auto-fills Municipality/Post-Admin/Suco. Click the map (or drag the pin) to adjust it." />
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={22}
                  maxNativeZoom={19}
                />

                {drawMode === 'polygon' && (
                    <FeatureGroup ref={featureGroupRef}>
                        <EditControl
                            position="topright"
                            onCreated={onCreated}
                            draw={{
                                polyline: false,
                                polygon: {
                                    shapeOptions: polygonShapeOptions,
                                    icon: polygonVertexIcon,
                                    touchIcon: polygonVertexIcon,
                                    showArea: false,
                                    allowIntersection: false,
                                },
                                rectangle: false,
                                circle: false,
                                marker: false,
                                circlemarker: false,
                            }}
                            edit={{
                                edit: false,
                                remove: false,
                            }}
                        />
                        {drawnItems && <GeoJSON data={drawnItems.toGeoJSON()} style={polygonShapeOptions} />}
                    </FeatureGroup>
                )}

                <PanToLatLng target={geoFlyTarget} />
                <TwoPointMapHandler
                    twoPoints={twoPoints}
                    setTwoPoints={setTwoPoints}
                    setFormData={setFormData}
                    drawMode={drawMode}
                    onPointMoved={handlePointMoved}
                />
                <FormMap
                    markerPosition={markerPosition}
                    setMarkerPosition={setMarkerPosition}
                    setFormData={setFormData}
                    drawMode={drawMode}
                    onPointMoved={handlePointMoved}
                    onBufferCleared={() => setDrawnItems(null)}
                />
              </MapContainer>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Cyclone Characteristics */}
        <Box>
          <SectionLabel text="Cyclone Characteristics" />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <TextField
              label="Date of Event"
              type="date"
              size="small"
              fullWidth
              sx={inputSx}
              name="date_cyclone"
              value={formData.date_cyclone}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Time of Event"
              type="time"
              size="small"
              fullWidth
              sx={inputSx}
              name="time_cyclone"
              value={formData.time_cyclone}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <FormControl>
              <FieldLabel text="Is this date and time of occurrence or observation?" />
              <RadioGroup row value={formData.time_type} onChange={handleChange} name="time_type">
                <FormControlLabel
                  value="Occurrence"
                  control={<Radio size="small" sx={radioSx} />}
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Occurrence</Typography>}
                  sx={{ mr: 3 }}
                />
                <FormControlLabel
                  value="Observation"
                  control={<Radio size="small" sx={radioSx} />}
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Observation</Typography>}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Maximum Wind Speed (observed)"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="wind_speed"
              value={formData.wind_speed}
              onChange={handleChange}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a wind speed category</em></MenuItem>
              <MenuItem value="Gentle breeze">Gentle breeze</MenuItem>
              <MenuItem value="Moderate breeze">Moderate breeze</MenuItem>
              <MenuItem value="Strong breeze">Strong breeze</MenuItem>
              <MenuItem value="Gale">Gale</MenuItem>
              <MenuItem value="Storm">Storm</MenuItem>
              <MenuItem value="Hurricane">Hurricane</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
            <TextField
              label="Approximate Rainfall"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="rainfall_amount"
              value={formData.rainfall_amount}
              onChange={handleChange}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a rainfall category</em></MenuItem>
              <MenuItem value="Light">Light</MenuItem>
              <MenuItem value="Moderate">Moderate</MenuItem>
              <MenuItem value="Heavy">Heavy</MenuItem>
              <MenuItem value="Extreme">Extreme</MenuItem>
            </TextField>

            <TextField
              label="Approximate Storm Surge Height"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="storm_surge_height"
              value={formData.storm_surge_height}
              onChange={handleChange}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a storm surge height</em></MenuItem>
              <MenuItem value="0 to 0.1 metres">0 to 0.1 metres</MenuItem>
              <MenuItem value="0.1 to 0.5 metres">0.1 to 0.5 metres</MenuItem>
              <MenuItem value="0.5 to 1.25 metres">0.5 to 1.25 metres</MenuItem>
              <MenuItem value="1.25 to 2.5 metres">1.25 to 2.5 metres</MenuItem>
              <MenuItem value="2.5 to 4 metres">2.5 to 4 metres</MenuItem>
              <MenuItem value="4 to 6 metres">4 to 6 metres</MenuItem>
              <MenuItem value="6 to 9 metres">6 to 9 metres</MenuItem>
              <MenuItem value="9 to 14 metres">9 to 14 metres</MenuItem>
              <MenuItem value="Over 14 metres">Over 14 metres</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <FormControl>
              <FieldLabel text="Type of Damage Caused (check all that apply)" />
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.damage_type.includes('Structural damage')}
                      onChange={handleChange}
                      name="damage_type"
                      value="Structural damage"
                      sx={checkSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Structural damage</Typography>}
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.damage_type.includes('Flooding')}
                      onChange={handleChange}
                      name="damage_type"
                      value="Flooding"
                      sx={checkSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Flooding</Typography>}
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.damage_type.includes('Disrupted services')}
                      onChange={handleChange}
                      name="damage_type"
                      value="Disrupted services"
                      sx={checkSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Disrupted services</Typography>}
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.damage_type.includes('Storm surge')}
                      onChange={handleChange}
                      name="damage_type"
                      value="Storm surge"
                      sx={checkSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Storm surge</Typography>}
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.damage_type.includes('Agricultural damage')}
                      onChange={handleChange}
                      name="damage_type"
                      value="Agricultural damage"
                      sx={checkSx}
                    />
                  }
                  label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Agricultural damage</Typography>}
                />
              </FormGroup>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
            <TextField
              label="Did you receive any official warnings before the cyclone?"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="warning_received"
              value={formData.warning_received}
              onChange={handleChange}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select an option</em></MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>

            <TextField
              label="Were you directly affected by the reported cyclone?"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="affected_status"
              value={formData.affected_status}
              onChange={handleChange}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select an option</em></MenuItem>
              <MenuItem value="Yes, directly impacted">Yes, directly impacted</MenuItem>
              <MenuItem value="No, but observed it from a safe distance">No, but observed it from a safe distance</MenuItem>
              <MenuItem value="No, and observed it after it happened">No, and observed it after it happened</MenuItem>
            </TextField>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Losses & Damages */}
        <Box>
          <SectionLabel text="Losses & Damages" />
          <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1.5 }}>Approximate figures are fine — leave blank if unknown.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 2.5 }}>
            <TextField label="People injured (approx.)" type="number" size="small" fullWidth sx={inputSx} name="people_injured_count" value={formData.people_injured_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Deaths reported (approx.)" type="number" size="small" fullWidth sx={inputSx} name="people_deaths_count" value={formData.people_deaths_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Houses affected (approx.)" type="number" size="small" fullWidth sx={inputSx} name="houses_affected_count" value={formData.houses_affected_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Livestock lost (approx.)" type="number" size="small" fullWidth sx={inputSx} name="livestock_lost_count" value={formData.livestock_lost_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Estimated monetary loss (USD)" type="number" size="small" fullWidth sx={inputSx} name="estimated_loss_amount" value={formData.estimated_loss_amount} onChange={handleChange} inputProps={{ min: 0, step: '0.01' }} />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Description */}
        <Box>
          <SectionLabel text="Description" />
          <FieldLabel text="Describe what happened" required />
          <TextField
            multiline
            rows={4}
            fullWidth
            required
            sx={inputSx}
            name="additional_info"
            value={formData.additional_info}
            onChange={handleChange}
            placeholder="Provide a description of this report"
          />
        </Box>

        {/* Submit Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleReset}
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': { borderColor: '#64748b', background: '#f8fafc' },
              px: 4,
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              background: '#2563eb',
              '&:hover': { background: '#1d4ed8' },
              px: 4,
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            Submit Report
          </Button>
        </Box>
      </Box>

      {/* Image Popup */}
      {showImagePopup && (
        <Box className="image-popup-overlay" onClick={() => setShowImagePopup(false)}>
          <Box className="image-popup-content" onClick={e => e.stopPropagation()}>
            <Box className="image-popup-close" onClick={() => setShowImagePopup(false)}>
              &times;
            </Box>
            <img src="/cyclone_wind_scale.png" alt="Saffir-Simpson Hurricane Wind Scale" />
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default FeltReport;